'use client';

import { useEffect, useState } from 'react';
import {
  DEFAULT_SCHEMA,
  FIELD_TYPE_OPTIONS,
  generateRandomCsv,
  type FieldType,
  type SchemaField,
} from '@/lib/browser-mockdata-extra-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const DELIMITERS: Record<string, string> = { comma: ',', semicolon: ';', tab: '\t', pipe: '|' };

export default function BrowserRandomCsvGenerator() {
  const [columns, setColumns] = useState<SchemaField[]>(() =>
    DEFAULT_SCHEMA.map((f) => ({ ...f })),
  );
  const [count, setCount] = useState(10);
  const [delimiter, setDelimiter] = useState('comma');
  const [output, setOutput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  function generate() {
    setCopyMessage(null);
    setOutput(generateRandomCsv(columns, count, DELIMITERS[delimiter] ?? ','));
  }

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateColumn(index: number, patch: Partial<SchemaField>) {
    setColumns((prev) => prev.map((col, i) => (i === index ? { ...col, ...patch } : col)));
  }

  function addColumn() {
    setColumns((prev) => [...prev, { name: `column_${prev.length + 1}`, type: 'word' }]);
  }

  function removeColumn(index: number) {
    setColumns((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Random CSV</span>
        <h2 className="tool-live-title">Random CSV data generator</h2>
        <p className="tool-live-subtitle">
          Choose columns and their types, set a row count, and generate a CSV with a header row
          locally in your browser. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {columns.map((column, index) => (
              <div className="tool-live-field tool-live-field-full" key={index}>
                <span>Column {index + 1}</span>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <input
                    aria-label={`Column ${index + 1} name`}
                    onChange={(event) => updateColumn(index, { name: event.target.value })}
                    placeholder="Column name"
                    style={{ flex: '1 1 8rem' }}
                    type="text"
                    value={column.name}
                  />
                  <select
                    aria-label={`Column ${index + 1} type`}
                    onChange={(event) =>
                      updateColumn(index, { type: event.target.value as FieldType })
                    }
                    style={{ flex: '1 1 8rem' }}
                    value={column.type}
                  >
                    {FIELD_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    aria-label={`Remove column ${index + 1}`}
                    className="button button-secondary"
                    disabled={columns.length <= 1}
                    onClick={() => removeColumn(index)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <label className="tool-live-field">
              <span>How many rows</span>
              <input
                inputMode="numeric"
                onChange={(event) => setCount(Number(event.target.value))}
                type="number"
                value={count}
              />
            </label>
            <label className="tool-live-field">
              <span>Delimiter</span>
              <select onChange={(event) => setDelimiter(event.target.value)} value={delimiter}>
                <option value="comma">Comma (,)</option>
                <option value="semicolon">Semicolon (;)</option>
                <option value="tab">Tab</option>
                <option value="pipe">Pipe (|)</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={addColumn} type="button">
              Add column
            </button>
            <button className="button button-secondary" onClick={generate} type="button">
              Generate
            </button>
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            ) : null}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Generated CSV</span>
            <textarea readOnly rows={16} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Click generate for a fresh set (max 1000 rows).
          </p>
        </section>
      </div>
    </div>
  );
}
